import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Globe, Zap, Plane } from 'lucide-react';

/**
 * Premium 3D Globe with animated flight route Madrid → Mexico
 * Uses spherical projection with rotation, glowing markers and animated plane
 */
const Globe3D = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrame;
    let rotation = 0.5; // Initial rotation for Atlantic view (Madrid + Mexico visible)
    let planeProgress = 0;
    
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();

    // Convert lat/lon to 3D point on unit sphere, then project to 2D
    // rotation is around Y axis
    const project = (lat, lon, cx, cy, radius, rot) => {
      const latRad = (lat * Math.PI) / 180;
      const lonRad = ((lon + rot * 57.2958) * Math.PI) / 180;
      
      // Sphere coords
      const x3d = Math.cos(latRad) * Math.sin(lonRad);
      const y3d = Math.sin(latRad);
      const z3d = Math.cos(latRad) * Math.cos(lonRad);
      
      // Slight isometric tilt (rotate around X by ~15deg)
      const tilt = -0.25;
      const y3dT = y3d * Math.cos(tilt) - z3d * Math.sin(tilt);
      const z3dT = y3d * Math.sin(tilt) + z3d * Math.cos(tilt);
      
      const screenX = cx + x3d * radius;
      const screenY = cy - y3dT * radius;
      
      return { x: screenX, y: screenY, z: z3dT, visible: z3dT > -0.1 };
    };

    // Continents data - sparse but recognizable
    const continents = {
      europe: [
        // Iberian Peninsula
        [36, -9], [40, -8], [43, -8], [43, -4], [42, -2], [40, 0], [38, 0], [37, -2], [36, -6],
        // Western Europe
        [49, -5], [51, 1], [53, 5], [54, 9], [50, 14], [48, 16], [45, 14], [43, 8], [44, 2],
        // Scandinavia
        [58, 8], [60, 15], [65, 22], [69, 26], [68, 18],
        // Eastern Europe  
        [54, 19], [52, 24], [50, 30], [48, 38], [44, 40],
        // Italy
        [46, 11], [42, 13], [40, 17], [38, 16],
        // UK
        [51, -1], [55, -4], [58, -5], [53, -3],
      ],
      africa: [
        [35, -6], [32, 0], [30, 10], [32, 22], [30, 32],
        [20, -16], [15, -10], [10, 0], [8, 10], [12, 30], [16, 35],
        [0, 10], [-5, 20], [-10, 25], [-15, 28], [-20, 30],
        [-25, 25], [-30, 22], [-33, 18], [-34, 24],
      ],
      northAmerica: [
        // Canada
        [60, -130], [62, -115], [60, -100], [58, -90], [55, -75], [60, -65],
        [65, -150], [70, -130], [70, -100], [70, -80],
        // USA
        [49, -125], [48, -100], [46, -85], [44, -75], [40, -75], [40, -90], [40, -100], [40, -115],
        [35, -80], [32, -90], [30, -95], [28, -98],
        // Mexico
        [22, -100], [19, -99], [16, -92], [20, -88], [25, -110],
      ],
      southAmerica: [
        [10, -75], [5, -75], [0, -70], [-5, -75], [-10, -75],
        [-15, -70], [-20, -65], [-22, -69], [-25, -70],
        [-30, -65], [-33, -70], [-35, -65], [-40, -65], [-50, -70],
        [-5, -60], [-10, -55], [-15, -50], [-20, -45], [-23, -47],
        [-28, -50], [-32, -55], [-30, -60],
        [0, -50], [5, -55],
      ],
      asia: [
        [50, 60], [55, 80], [60, 100], [55, 120], [50, 140],
        [40, 75], [35, 85], [30, 90], [28, 100], [25, 110],
        [20, 105], [15, 100], [10, 105],
        [35, 130], [30, 140],
        [25, 55], [22, 50], [15, 45],
      ],
    };

    const drawGlobe = (cx, cy, radius) => {
      // Outer sphere outline (subtle)
      ctx.strokeStyle = 'rgba(184, 146, 63, 0.15)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      
      // Inner atmosphere glow
      const atmGrad = ctx.createRadialGradient(cx, cy, radius * 0.9, cx, cy, radius * 1.1);
      atmGrad.addColorStop(0, 'rgba(184, 146, 63, 0)');
      atmGrad.addColorStop(0.6, 'rgba(184, 146, 63, 0.12)');
      atmGrad.addColorStop(1, 'rgba(184, 146, 63, 0)');
      ctx.fillStyle = atmGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 1.1, 0, Math.PI * 2);
      ctx.fill();
      
      // Globe surface gradient (sphere shading)
      const surfGrad = ctx.createRadialGradient(
        cx - radius * 0.3, cy - radius * 0.3, 0,
        cx, cy, radius
      );
      surfGrad.addColorStop(0, 'rgba(35, 28, 18, 0.6)');
      surfGrad.addColorStop(0.7, 'rgba(15, 12, 8, 0.95)');
      surfGrad.addColorStop(1, 'rgba(5, 5, 5, 1)');
      ctx.fillStyle = surfGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawGrid = (cx, cy, radius, rot) => {
      ctx.strokeStyle = 'rgba(184, 146, 63, 0.08)';
      ctx.lineWidth = 0.5;
      
      // Meridians (lines of longitude)
      for (let lon = -180; lon < 180; lon += 20) {
        ctx.beginPath();
        let started = false;
        for (let lat = -90; lat <= 90; lat += 3) {
          const p = project(lat, lon, cx, cy, radius, rot);
          if (p.visible) {
            if (!started) {
              ctx.moveTo(p.x, p.y);
              started = true;
            } else {
              ctx.lineTo(p.x, p.y);
            }
          } else {
            started = false;
          }
        }
        ctx.stroke();
      }
      
      // Parallels (lines of latitude)
      for (let lat = -60; lat <= 60; lat += 20) {
        ctx.beginPath();
        let started = false;
        for (let lon = -180; lon <= 180; lon += 3) {
          const p = project(lat, lon, cx, cy, radius, rot);
          if (p.visible) {
            if (!started) {
              ctx.moveTo(p.x, p.y);
              started = true;
            } else {
              ctx.lineTo(p.x, p.y);
            }
          } else {
            started = false;
          }
        }
        ctx.stroke();
      }
    };

    const drawContinents = (cx, cy, radius, rot) => {
      Object.values(continents).forEach(continent => {
        continent.forEach(([lat, lon]) => {
          const p = project(lat, lon, cx, cy, radius, rot);
          if (p.visible) {
            const alpha = Math.min(1, (p.z + 0.2) * 1.5);
            ctx.fillStyle = `rgba(184, 146, 63, ${alpha * 0.45})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2);
            ctx.fill();
          }
        });
      });
    };

    // Markers data - Madrid as hub, destinations across Europe & LATAM
    const markers = [
      { name: 'MADRID', lat: 40.4, lon: -3.7, type: 'hub' },
      // LATAM destinations (burgundy routes)
      { name: 'MEXICO', lat: 19.4, lon: -99.1, type: 'latam' },
      { name: 'BOGOTÁ', lat: 4.7, lon: -74.1, type: 'latam' },
      { name: 'BUENOS AIRES', lat: -34.6, lon: -58.4, type: 'latam' },
      { name: 'SÃO PAULO', lat: -23.5, lon: -46.6, type: 'latam' },
      // Europe destinations (gold routes)
      { name: 'LISBON', lat: 38.7, lon: -9.1, type: 'europe' },
      { name: 'PARIS', lat: 48.9, lon: 2.3, type: 'europe' },
      { name: 'ROME', lat: 41.9, lon: 12.5, type: 'europe' },
    ];

    const drawMarkers = (cx, cy, radius, rot, time) => {
      markers.forEach(m => {
        const p = project(m.lat, m.lon, cx, cy, radius, rot);
        if (!p.visible) return;
        
        const isHub = m.type === 'hub';
        const colorRGB = m.type === 'latam' ? '197, 104, 117' : '184, 146, 63';
        
        if (isHub) {
          // Hub: Madrid - extra prominent with double pulse
          const pulseT1 = (time / 1500) % 1;
          const pulseT2 = ((time / 1500) + 0.5) % 1;
          
          ctx.strokeStyle = `rgba(${colorRGB}, ${1 - pulseT1})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4 + pulseT1 * 18, 0, Math.PI * 2);
          ctx.stroke();
          
          ctx.strokeStyle = `rgba(${colorRGB}, ${(1 - pulseT2) * 0.5})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4 + pulseT2 * 18, 0, Math.PI * 2);
          ctx.stroke();
          
          // Glow
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 18);
          glow.addColorStop(0, `rgba(${colorRGB}, 0.7)`);
          glow.addColorStop(1, `rgba(${colorRGB}, 0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 18, 0, Math.PI * 2);
          ctx.fill();
          
          // Core
          ctx.fillStyle = `rgba(${colorRGB}, 1)`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
          ctx.fill();
          
          // Label
          ctx.font = '600 10px JetBrains Mono';
          ctx.fillStyle = '#FFFFFF';
          ctx.textAlign = 'center';
          ctx.fillText(m.name, p.x, p.y - 16);
        } else {
          // Destinations: pulse glow + glowing dot
          const pulseT = ((time / 2000) + (m.name.length * 0.1)) % 1;
          
          ctx.strokeStyle = `rgba(${colorRGB}, ${(1 - pulseT) * 0.7})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2 + pulseT * 8, 0, Math.PI * 2);
          ctx.stroke();
          
          // Glow
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 8);
          glow.addColorStop(0, `rgba(${colorRGB}, 0.5)`);
          glow.addColorStop(1, `rgba(${colorRGB}, 0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
          ctx.fill();
          
          // Core
          ctx.fillStyle = `rgba(${colorRGB}, 0.95)`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(p.x, p.y, 1, 0, Math.PI * 2);
          ctx.fill();
          
          // Label - smaller
          ctx.font = '500 9px JetBrains Mono';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
          ctx.textAlign = 'center';
          ctx.fillText(m.name, p.x, p.y - 10);
        }
      });
    };
    
    // Compute great circle arc points from A to B
    const greatCircle = (lat1, lon1, lat2, lon2, steps = 60) => {
      const points = [];
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const λ1 = (lon1 * Math.PI) / 180;
      const λ2 = (lon2 * Math.PI) / 180;
      
      const d = 2 * Math.asin(Math.sqrt(
        Math.sin((φ2 - φ1) / 2) ** 2 +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2
      ));
      
      for (let i = 0; i <= steps; i++) {
        const f = i / steps;
        const A = Math.sin((1 - f) * d) / Math.sin(d);
        const B = Math.sin(f * d) / Math.sin(d);
        
        const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
        const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
        const z = A * Math.sin(φ1) + B * Math.sin(φ2);
        
        const lat = Math.atan2(z, Math.sqrt(x * x + y * y)) * 180 / Math.PI;
        const lon = Math.atan2(y, x) * 180 / Math.PI;
        points.push({ lat, lon });
      }
      return points;
    };

    // Helper: draw a single great-circle route with elevated arc
    const drawSingleRoute = (cx, cy, radius, rot, latA, lonA, latB, lonB, colorRGB, showPlane, time) => {
      const arc = greatCircle(latA, lonA, latB, lonB, 60);
      const projected = arc.map(p => {
        const proj = project(p.lat, p.lon, cx, cy, radius, rot);
        return { ...proj, lat: p.lat, lon: p.lon };
      });
      
      // Build elevated path (arc rises above globe)
      const elevatedPath = projected.map((p, i) => {
        const t = i / (projected.length - 1);
        const elevation = Math.sin(t * Math.PI) * radius * 0.16;
        const dx = p.x - cx;
        const dy = p.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const scale = dist > 0 ? (dist + elevation) / dist : 1;
        return {
          x: cx + dx * scale,
          y: cy + dy * scale - elevation * 0.25,
          visible: p.visible,
        };
      });
      
      // Draw route line
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (let i = 0; i < elevatedPath.length - 1; i++) {
        const p1 = elevatedPath[i];
        const p2 = elevatedPath[i + 1];
        if (!p1.visible || !p2.visible) continue;
        
        const t = i / (elevatedPath.length - 1);
        // Fade in/out at endpoints
        const fade = Math.min(1, Math.min(t, 1 - t) * 4);
        const alpha = 0.55 * fade;
        
        ctx.strokeStyle = `rgba(${colorRGB}, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
      
      // Animated data pulse traveling along the route (subtle data flow effect)
      const pulseSpeed = 0.0005;
      const pulseT = ((time * pulseSpeed) % 1);
      const pulseIdx = Math.floor(pulseT * (elevatedPath.length - 1));
      
      // Pulse trail (5-8 points)
      for (let i = 0; i < 12; i++) {
        const idx = pulseIdx - i;
        if (idx < 0 || idx >= elevatedPath.length) continue;
        const tp = elevatedPath[idx];
        if (!tp.visible) continue;
        const a = (1 - i / 12) * 0.7;
        ctx.fillStyle = `rgba(${colorRGB}, ${a})`;
        ctx.beginPath();
        ctx.arc(tp.x, tp.y, 1.3 * (1 - i / 12), 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Animated plane on the main route only
      if (showPlane) {
        planeProgress += 0.0028;
        if (planeProgress > 1.15) planeProgress = -0.1;
        
        if (planeProgress >= 0 && planeProgress <= 1) {
          const idx = Math.floor(planeProgress * (elevatedPath.length - 1));
          const planePoint = elevatedPath[idx];
          const nextPoint = elevatedPath[Math.min(idx + 1, elevatedPath.length - 1)];
          
          if (planePoint.visible) {
            // Trail behind plane
            for (let i = 0; i < 22; i++) {
              const trailIdx = idx - i;
              if (trailIdx < 0) break;
              const tp = elevatedPath[trailIdx];
              if (!tp.visible) continue;
              const alpha = (1 - i / 22) * 0.55;
              ctx.fillStyle = `rgba(255, 245, 220, ${alpha})`;
              ctx.beginPath();
              ctx.arc(tp.x, tp.y, 1 * (1 - i / 22), 0, Math.PI * 2);
              ctx.fill();
            }
            
            // Glow under plane
            const glow = ctx.createRadialGradient(planePoint.x, planePoint.y, 0, planePoint.x, planePoint.y, 16);
            glow.addColorStop(0, 'rgba(255, 245, 220, 0.6)');
            glow.addColorStop(1, 'rgba(255, 245, 220, 0)');
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(planePoint.x, planePoint.y, 16, 0, Math.PI * 2);
            ctx.fill();
            
            // Plane shape
            const dx = nextPoint.x - planePoint.x;
            const dy = nextPoint.y - planePoint.y;
            const angle = Math.atan2(dy, dx);
            
            ctx.save();
            ctx.translate(planePoint.x, planePoint.y);
            ctx.rotate(angle);
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath();
            ctx.moveTo(5, 0);
            ctx.lineTo(-3, -2.5);
            ctx.lineTo(-1.5, 0);
            ctx.lineTo(-3, 2.5);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
        }
      }
    };
    
    const drawFlightRoute = (cx, cy, radius, rot, time) => {
      const madrid = { lat: 40.4, lon: -3.7 };
      
      // All destinations from Madrid
      const routes = [
        // LATAM (burgundy)
        { name: 'mexico', lat: 19.4, lon: -99.1, color: '197, 104, 117', plane: true }, // Main route with plane
        { name: 'bogota', lat: 4.7, lon: -74.1, color: '197, 104, 117' },
        { name: 'buenosaires', lat: -34.6, lon: -58.4, color: '197, 104, 117' },
        { name: 'saopaulo', lat: -23.5, lon: -46.6, color: '197, 104, 117' },
        // Europe (gold)
        { name: 'lisbon', lat: 38.7, lon: -9.1, color: '184, 146, 63' },
        { name: 'paris', lat: 48.9, lon: 2.3, color: '184, 146, 63' },
        { name: 'rome', lat: 41.9, lon: 12.5, color: '184, 146, 63' },
      ];
      
      routes.forEach(r => {
        drawSingleRoute(cx, cy, radius, rot, madrid.lat, madrid.lon, r.lat, r.lon, r.color, r.plane, time);
      });
    };
    
    const animate = (time) => {
      const w = canvas.width / window.devicePixelRatio;
      const h = canvas.height / window.devicePixelRatio;
      
      // Clear with deep background
      ctx.clearRect(0, 0, w, h);
      
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.38;
      
      // Rotation (very slow for cinematic feel)
      rotation += 0.0003;
      
      drawGlobe(cx, cy, radius);
      drawGrid(cx, cy, radius, rotation);
      drawContinents(cx, cy, radius, rotation);
      drawFlightRoute(cx, cy, radius, rotation, time);
      drawMarkers(cx, cy, radius, rotation, time);
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);
    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame);
    };
  }, []);
  
  return (
    <div className="relative w-full aspect-square rounded-3xl overflow-hidden border border-white/10" style={{
      background: 'radial-gradient(ellipse at center, #14100A 0%, #050505 100%)'
    }}>
      <canvas ref={canvasRef} className="w-full h-full" />
      
      {/* Overlay UI - top */}
      <div className="absolute top-5 left-5 flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-gold/30">
          <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-dot" />
          <span className="font-mono text-[9px] tracking-widest uppercase text-gold">GLOBAL NETWORK</span>
        </div>
      </div>
      
      <div className="absolute top-5 right-5 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-white/10">
        <span className="font-mono text-[9px] tracking-widest uppercase text-white/70">7 rutas activas</span>
      </div>
      
      {/* Bottom: route info */}
      <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gold" />
            <span className="font-mono text-[9px] tracking-widest uppercase text-white/50">Europa</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#C56875' }} />
            <span className="font-mono text-[9px] tracking-widest uppercase text-white/50">LATAM</span>
          </div>
        </div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-white/40">
          Wingfold · Global
        </div>
      </div>
    </div>
  );
};

export const AboutSection = () => {
  return (
    <section id="nosotros" className="relative py-32 px-6 lg:px-12 bg-[#0A0A0A]">
      {/* Decorative elements contained separately so text italic overhang is not clipped */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-20" />
      </div>
      
      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="font-mono text-burgundy-300 text-xs tracking-[0.3em] uppercase mb-6 flex items-center gap-3">
              <span className="w-12 h-px bg-burgundy-300" />
              Global Network
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-5xl font-light tracking-tight mb-8 leading-[1.1] pb-1 text-white">
              Operamos desde
              <br />
              <span className="italic font-medium" style={{ color: '#C56875' }}>Madrid</span>
              <span className="text-gold">.</span>
              <br />
              <span className="text-white">Volamos </span>
              <span className="italic font-medium text-gold-shimmer" style={{ paddingRight: '0.1em' }}>global</span>
              <span className="text-burgundy-300">.</span>
            </h2>
            
            <p className="font-sans text-lg text-white/60 mb-10 leading-relaxed">
              Una red activa de mercados estratégicos en Europa y LATAM. 
              Rutas de impacto reales, sin intermediarios, gestionadas en tiempo real.
            </p>

            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-start gap-5 group"
                data-testid="location-madrid"
              >
                <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold group-hover:border-gold transition-all duration-300">
                  <MapPin className="w-5 h-5 text-gold group-hover:text-black transition-colors" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-medium tracking-tight text-white mb-1">
                    Hub Central: Madrid
                  </h3>
                  <p className="font-sans text-base text-white/50 leading-relaxed">
                    Operación centralizada en el corazón de Europa. 
                    Latencia mínima hacia ambos continentes.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex items-start gap-5 group"
                data-testid="reach-europe"
              >
                <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-burgundy-300/30 flex items-center justify-center flex-shrink-0 group-hover:bg-burgundy group-hover:border-burgundy transition-all duration-300">
                  <Globe className="w-5 h-5 text-burgundy-300 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-medium tracking-tight text-white mb-1">
                    Cobertura: Europa & LATAM
                  </h3>
                  <p className="font-sans text-base text-white/50 leading-relaxed">
                    Mercados conectados. Buying programático y direct deals con publishers premium en cada región.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex items-start gap-5 group"
                data-testid="impact-global"
              >
                <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-gold/30 flex items-center justify-center flex-shrink-0 group-hover:bg-gold group-hover:border-gold transition-all duration-300">
                  <Zap className="w-5 h-5 text-gold group-hover:text-black transition-colors" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-medium tracking-tight text-white mb-1">
                    Performance en tiempo real
                  </h3>
                  <p className="font-sans text-base text-white/50 leading-relaxed">
                    Cada ruta optimizada con datos. Cada inversión medida. Cada resultado, reportado.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* 3D Globe with animated flight route */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative"
            data-testid="global-map-visual"
          >
            <Globe3D />
            
            {/* Outer glow */}
            <div className="absolute -inset-4 -z-10 rounded-3xl opacity-50 blur-3xl" style={{
              background: 'radial-gradient(circle at center, rgba(184, 146, 63, 0.3) 0%, transparent 70%)'
            }} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
